import React, { useState, useMemo, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const RetailerCollectionReports = () => {
  const [collections, setCollections] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: {
      start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0]
    },
    retailerFilter: '',
    paymentMethod: '',
    collectionType: '',
    amountRange: { min: '', max: '' },
    reportType: 'detailed'
  });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Mock data focused on retailer collections
  const mockCollections = [
    {
      id: 'COL001',
      date: '2024-01-15',
      retailerId: 'RET001',
      retailerName: 'Smart Mobile Store',
      retailerAddress: 'Shop No. 15, Main Market, Delhi',
      retailerPhone: '+91 9876543210',
      amount: 25000,
      outstandingAmount: 50000,
      paymentMethod: 'UPI',
      referenceNumber: 'TXN123456',
      collectionType: 'Outstanding Payment',
      collectedBy: 'Vikram Singh (FOS)',
      commission: 625,
      status: 'Completed',
      remarks: 'Partial payment received'
    },
    {
      id: 'COL002',
      date: '2024-01-14',
      retailerId: 'RET003',
      retailerName: 'Digital World',
      retailerAddress: 'Plot 45, Electronics Market, Mumbai',
      retailerPhone: '+91 9876543211',
      amount: 18000,
      outstandingAmount: 30000,
      paymentMethod: 'Bank Transfer',
      referenceNumber: 'TXN123457',
      collectionType: 'Outstanding Payment',
      collectedBy: 'Anita Desai (FOS)',
      commission: 360,
      status: 'Completed',
      remarks: 'Regular monthly payment'
    },
    {
      id: 'COL003',
      date: '2024-01-13',
      retailerId: 'RET002',
      retailerName: 'Tech Zone Electronics',
      retailerAddress: 'Unit 23, Tech Plaza, Bangalore',
      retailerPhone: '+91 9876543212',
      amount: 15000,
      outstandingAmount: 25000,
      paymentMethod: 'Cash',
      referenceNumber: '',
      collectionType: 'Outstanding Payment',
      collectedBy: 'Vikram Singh (FOS)',
      commission: 375,
      status: 'Completed',
      remarks: 'Cash payment at store'
    },
    {
      id: 'COL004',
      date: '2024-01-12',
      retailerId: 'RET004',
      retailerName: 'Mobile Hub',
      retailerAddress: 'Shop 67, Mobile Street, Chennai',
      retailerPhone: '+91 9876543213',
      amount: 12000,
      outstandingAmount: 40000,
      paymentMethod: 'Cheque',
      referenceNumber: 'CHQ001',
      collectionType: 'Advance Payment',
      collectedBy: 'Rohan Mehta (FOS)',
      commission: 264,
      status: 'Pending',
      remarks: 'Cheque under clearance'
    },
    {
      id: 'COL005',
      date: '2024-01-11',
      retailerId: 'RET005',
      retailerName: 'Electronic Store',
      retailerAddress: '88, Electronics Hub, Pune',
      retailerPhone: '+91 9876543214',
      amount: 8000,
      outstandingAmount: 15000,
      paymentMethod: 'UPI',
      referenceNumber: 'TXN123458',
      collectionType: 'Outstanding Payment',
      collectedBy: 'Anita Desai (FOS)',
      commission: 160,
      status: 'Completed',
      remarks: 'Quick UPI payment'
    }
  ];

  const retailersList = [
    { id: 'RET001', name: 'Smart Mobile Store' },
    { id: 'RET002', name: 'Tech Zone Electronics' },
    { id: 'RET003', name: 'Digital World' },
    { id: 'RET004', name: 'Mobile Hub' },
    { id: 'RET005', name: 'Electronic Store' }
  ];

  useEffect(() => {
    setCollections(mockCollections);
  }, []);

  const filteredData = useMemo(() => {
    let filtered = collections;

    // Date Range Filter
    if (filters.dateRange.start && filters.dateRange.end) {
      const startDate = new Date(filters.dateRange.start);
      const endDate = new Date(filters.dateRange.end);
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    // Retailer Filter
    if (filters.retailerFilter) {
      filtered = filtered.filter(item => item.retailerId === filters.retailerFilter);
    }

    // Payment Method Filter
    if (filters.paymentMethod) {
      filtered = filtered.filter(item => item.paymentMethod === filters.paymentMethod);
    }

    // Collection Type Filter
    if (filters.collectionType) {
      filtered = filtered.filter(item => item.collectionType === filters.collectionType);
    }

    // Amount Range Filter
    if (filters.amountRange.min) {
      filtered = filtered.filter(item => item.amount >= parseFloat(filters.amountRange.min));
    }
    if (filters.amountRange.max) {
      filtered = filtered.filter(item => item.amount <= parseFloat(filters.amountRange.max));
    }

    return filtered;
  }, [collections, filters]);

  const summaryData = useMemo(() => {
    const totalAmount = filteredData.reduce((sum, item) => sum + item.amount, 0);
    const totalOutstanding = filteredData.reduce((sum, item) => sum + item.outstandingAmount, 0);
    const totalCommission = filteredData.reduce((sum, item) => sum + item.commission, 0);
    const completedCollections = filteredData.filter(item => item.status === 'Completed');
    const pendingCollections = filteredData.filter(item => item.status === 'Pending');

    const retailerSummary = retailersList.map(retailer => {
      const retailerCollections = filteredData.filter(item => item.retailerId === retailer.id);
      const retailerTotal = retailerCollections.reduce((sum, item) => sum + item.amount, 0);
      const retailerOutstanding = retailerCollections.reduce((sum, item) => sum + item.outstandingAmount, 0);
      return {
        ...retailer,
        totalAmount: retailerTotal,
        totalOutstanding: retailerOutstanding,
        collectionCount: retailerCollections.length
      };
    });

    const paymentMethodSummary = ['Cash', 'UPI', 'Bank Transfer', 'Cheque', 'DD'].map(method => {
      const methodCollections = filteredData.filter(item => item.paymentMethod === method);
      const methodTotal = methodCollections.reduce((sum, item) => sum + item.amount, 0);
      return {
        method,
        totalAmount: methodTotal,
        count: methodCollections.length
      };
    });

    return {
      totalAmount,
      totalOutstanding,
      totalCommission,
      totalCollections: filteredData.length,
      completedAmount: completedCollections.reduce((sum, item) => sum + item.amount, 0),
      pendingAmount: pendingCollections.reduce((sum, item) => sum + item.amount, 0),
      retailerSummary,
      paymentMethodSummary: paymentMethodSummary.filter(item => item.count > 0)
    };
  }, [filteredData, retailersList]);

  const handleFilterChange = (category, field, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: typeof prev[category] === 'object' && !Array.isArray(prev[category])
        ? { ...prev[category], [field]: value }
        : value
    }));
  };

  const clearFilters = () => {
    setFilters({
      dateRange: {
        start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      },
      retailerFilter: '',
      paymentMethod: '',
      collectionType: '',
      amountRange: { min: '', max: '' },
      reportType: 'detailed'
    });
  };

  const generateReport = () => {
    const report = {
      summary: summaryData,
      collections: filteredData,
      filters: filters,
      generatedAt: new Date().toISOString()
    };
    setReportData(report);
    setShowPreviewModal(true);
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Summary Sheet
    const summarySheet = [
      ['Retailer Collection Report Summary'],
      ['Generated On:', new Date().toLocaleDateString()],
      ['Period:', `${filters.dateRange.start} to ${filters.dateRange.end}`],
      [''],
      ['Total Collections:', summaryData.totalCollections],
      ['Total Amount Collected:', `₹${summaryData.totalAmount.toLocaleString()}`],
      ['Total Outstanding:', `₹${summaryData.totalOutstanding.toLocaleString()}`],
      ['Total Commission:', `₹${summaryData.totalCommission.toLocaleString()}`],
      ['Completed Amount:', `₹${summaryData.completedAmount.toLocaleString()}`],
      ['Pending Amount:', `₹${summaryData.pendingAmount.toLocaleString()}`],
      [''],
      ['Retailer Performance:'],
      ['Retailer Name', 'Collections', 'Amount Collected', 'Outstanding'],
      ...summaryData.retailerSummary.map(retailer => [
        retailer.name,
        retailer.collectionCount,
        retailer.totalAmount,
        retailer.totalOutstanding
      ])
    ];

    const summaryWS = XLSX.utils.aoa_to_sheet(summarySheet);
    XLSX.utils.book_append_sheet(workbook, summaryWS, 'Summary');

    // Details Sheet
    const detailsWS = XLSX.utils.json_to_sheet(filteredData.map(item => ({
      'Collection ID': item.id,
      'Date': item.date,
      'Retailer Name': item.retailerName,
      'Retailer Address': item.retailerAddress,
      'Phone': item.retailerPhone,
      'Amount Collected': item.amount,
      'Outstanding Amount': item.outstandingAmount,
      'Payment Method': item.paymentMethod,
      'Reference': item.referenceNumber,
      'Collection Type': item.collectionType,
      'Collected By': item.collectedBy,
      'Commission': item.commission,
      'Status': item.status,
      'Remarks': item.remarks
    })));
    XLSX.utils.book_append_sheet(workbook, detailsWS, 'Retailer Collections');

    XLSX.writeFile(workbook, `retailer_collection_report_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Retailer collection report exported successfully');
  };

  const getStatusBadge = (status) => {
    return status === 'Completed' ? 
      <Badge bg="success">Completed</Badge> : 
      <Badge bg="warning">Pending</Badge>;
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-store me-2"></i>
          Retailer Collection Reports
        </h5>
        <Button variant="success" onClick={exportToExcel}>
          <i className="fas fa-file-excel me-2"></i>
          Export Excel
        </Button>
      </div>


      {/* Advanced Filters */}
      <Card className="mb-4 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
        <Card.Body>
          <h5 className="mb-3" style={{ color: '#031a6f' }}>
            <i className="fas fa-filter me-2"></i>
            Filter by Retailer Details
          </h5>
          <Row>
            <Col lg={4} md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Date Range</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="date"
                    value={filters.dateRange.start}
                    onChange={(e) => handleFilterChange('dateRange', 'start', e.target.value)}
                  />
                  <Form.Control
                    type="date"
                    value={filters.dateRange.end}
                    onChange={(e) => handleFilterChange('dateRange', 'end', e.target.value)}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Select Retailer</Form.Label>
                <Form.Select
                  value={filters.retailerFilter}
                  onChange={(e) => handleFilterChange('retailerFilter', null, e.target.value)}
                >
                  <option value="">All Retailers</option>
                  {retailersList.map(retailer => (
                    <option key={retailer.id} value={retailer.id}>{retailer.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col lg={2} md={4} className="mb-3">
              <Form.Group>
                <Form.Label>Payment Method</Form.Label>
                <Form.Select
                  value={filters.paymentMethod}
                  onChange={(e) => handleFilterChange('paymentMethod', null, e.target.value)}
                >
                  <option value="">All Methods</option>
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="DD">Demand Draft</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col lg={2} md={4} className="mb-3">
              <Form.Group>
                <Form.Label>Collection Type</Form.Label>
                <Form.Select
                  value={filters.collectionType}
                  onChange={(e) => handleFilterChange('collectionType', null, e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="Outstanding Payment">Outstanding Payment</option>
                  <option value="Advance Payment">Advance Payment</option>
                  <option value="Regular Payment">Regular Payment</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col lg={1} md={4} className="mb-3 d-flex align-items-end">
              <Button 
                variant="outline-secondary" 
                onClick={clearFilters} 
                className="w-100"
                title="Clear Filters"
              >
                <i className="fas fa-eraser"></i>
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Collections Table */}
      <h5 className="mb-3" style={{ color: '#031a6f' }}>
        <i className="fas fa-table me-2"></i>
        Retailer Collections Data ({filteredData.length} records)
      </h5>
      <div className="table-responsive">
        <Table striped bordered hover className="modern-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Collection ID</th>
              <th>Retailer Details</th>
              <th>Amount Collected</th>
              <th>Outstanding</th>
              <th>Payment Method</th>
              <th>Collected By</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((collection) => (
              <tr key={collection.id}>
                <td>{collection.date}</td>
                <td><strong>{collection.id}</strong></td>
                <td>
                  <div>
                    <strong>{collection.retailerName}</strong>
                    <br />
                    <small className="text-muted">{collection.retailerAddress}</small>
                    <br />
                    <small className="text-muted">{collection.retailerPhone}</small>
                  </div>
                </td>
                <td className="text-primary fw-bold">₹{collection.amount.toLocaleString()}</td>
                <td className="text-danger">₹{collection.outstandingAmount.toLocaleString()}</td>
                <td>{collection.paymentMethod}</td>
                <td>{collection.collectedBy}</td>
                <td>{getStatusBadge(collection.status)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Report Preview Modal */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Retailer Collection Report Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reportData && (
            <div>
              <div className="text-center mb-4">
                <h4>Retailer Collection Report</h4>
                <p className="text-muted">
                  Period: {filters.dateRange.start} to {filters.dateRange.end}
                </p>
              </div>

              <Row className="mb-4">
                <Col md={6}>
                  <h6>Summary</h6>
                  <ul className="list-unstyled">
                    <li>Total Collections: <strong>{reportData.summary.totalCollections}</strong></li>
                    <li>Amount Collected: <strong>₹{reportData.summary.totalAmount.toLocaleString()}</strong></li>
                    <li>Total Outstanding: <strong>₹{reportData.summary.totalOutstanding.toLocaleString()}</strong></li>
                    <li>Completed: <strong>₹{reportData.summary.completedAmount.toLocaleString()}</strong></li>
                    <li>Pending: <strong>₹{reportData.summary.pendingAmount.toLocaleString()}</strong></li>
                  </ul>
                </Col>
                <Col md={6}>
                  <h6>Retailer Performance</h6>
                  <Table size="sm">
                    <thead>
                      <tr>
                        <th>Retailer</th>
                        <th>Collections</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.summary.retailerSummary
                        .filter(retailer => retailer.collectionCount > 0)
                        .map((retailer, index) => (
                        <tr key={index}>
                          <td>{retailer.name}</td>
                          <td>{retailer.collectionCount}</td>
                          <td>₹{retailer.totalAmount.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
          <Button variant="success" onClick={exportToExcel}>
            <i className="fas fa-download me-2"></i>
            Export to Excel
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default RetailerCollectionReports;